import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatRelativeDate } from '../utils';

const statusClass = {
  confirmed: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-800',
  completed: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-rose-50 text-rose-700',
  scheduled: 'bg-sky-50 text-sky-700',
};

const ScheduledTours = ({ bookings = [], tours = [], isLoading }) => {
  const navigate = useNavigate();

  // Prefer workspace advisory bookings; fall back to legacy tour shape
  const items = (
    Array.isArray(bookings) && bookings.length
      ? bookings.map((b) => ({
          id: `b-${b.id}`,
          kind: 'booking',
          title: b.topic || 'Advisory session',
          subtitle: b.advisor?.name || b.advisor_name || 'Advisor',
          when: b.starts_at || b.scheduled_at,
          status: b.status || 'pending',
          href: '/workspace/bookings',
          mode: b.meeting_mode,
        }))
      : (Array.isArray(tours) ? tours : []).map((t) => ({
          id: `t-${t.id}`,
          kind: 'tour',
          title: t.property?.title || t.title || 'Property tour',
          subtitle: t.property?.location || t.agent?.name || t.agent || 'Tour',
          when: t.scheduled_date || t.date,
          time: t.time,
          status: t.status || 'pending',
          href: t.property?.slug_id
            ? `/property/${t.property.slug_id}`
            : t.property?.id
              ? `/property/${t.property.id}`
              : '/property-search-browse',
        }))
  ).slice(0, 5);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Bookings & tours</h2>
          <p className="text-xs text-slate-500 mt-0.5">Upcoming advisory and viewings</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="Calendar"
          iconPosition="left"
          onClick={() => navigate('/workspace/bookings')}
        >
          Book
        </Button>
      </div>

      {isLoading ? (
        <div className="p-5 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 px-5">
          <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="Calendar" size={20} className="text-amber-600" />
          </div>
          <p className="text-sm font-medium text-slate-900 mb-1">Nothing scheduled</p>
          <p className="text-xs text-slate-500 mb-4">
            Book a verified agent or schedule a property viewing.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/workspace/bookings')}
            iconName="CalendarPlus"
            iconPosition="left"
          >
            Book an advisor
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => {
            const st = String(item.status || 'pending').toLowerCase();
            return (
              <li key={item.id}>
                <Link
                  to={item.href}
                  className="flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50/80 transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0">
                    <Icon name={item.kind === 'booking' ? 'Video' : 'Home'} size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.title}</p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{item.subtitle}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      {item.when && (
                        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                          <Icon name="Clock" size={11} />
                          {formatRelativeDate(item.when) || item.when}
                          {item.time ? ` · ${item.time}` : ''}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                          statusClass[st] || statusClass.pending
                        }`}
                      >
                        {st}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ScheduledTours;
